import { useEffect, useState, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Particles from 'react-tsparticles';
import { loadSlim } from 'tsparticles-slim';
import './PluckLanding.css';

export default function PluckLanding() {
  const { i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [formData, setFormData] = useState({ name: '', email: '', phone: '' });
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [particlesLoaded, setParticlesLoaded] = useState(false);
  const [videoModalOpen, setVideoModalOpen] = useState(false);
  const [videoFullscreen, setVideoFullscreen] = useState(false);
  const [isYearlyBilling, setIsYearlyBilling] = useState(true);
  const [showPlanComparison, setShowPlanComparison] = useState(false);

  // Initialize particles
  const particlesInit = useCallback(async (engine) => {
    await loadSlim(engine);
  }, []);

  // Particles loaded callback
  const particlesLoadedCallback = useCallback(async (container) => {
    setParticlesLoaded(true);
  }, []);

  // Particles configuration - memoized to prevent re-renders
  const particlesOptions = useMemo(() => ({
    fullScreen: false,
    background: {
      color: {
        value: "transparent"
      }
    },
    fpsLimit: 60,
    particles: {
      number: {
        value: 100,
        density: {
          enable: true,
          area: 800
        }
      },
      color: {
        value: "#ffffff"
      },
      shape: {
        type: "circle"
      },
      opacity: {
        value: 0.6,
        random: true,
        animation: {
          enable: true,
          speed: 1,
          minimumValue: 0.2,
          sync: false
        }
      },
      size: {
        value: 4,
        random: true,
        animation: {
          enable: true,
          speed: 3,
          minimumValue: 1,
          sync: false
        }
      },
      links: {
        enable: true,
        distance: 150,
        color: "#ffffff",
        opacity: 0.4,
        width: 1
      },
      move: {
        enable: true,
        speed: 3,
        direction: "none",
        random: true,
        straight: false,
        outModes: {
          default: "out"
        },
        attract: {
          enable: false,
          rotateX: 600,
          rotateY: 1200
        }
      }
    },
    interactivity: {
      detectsOn: "canvas",
      events: {
        onHover: {
          enable: true,
          mode: "grab"
        },
        onClick: {
          enable: true,
          mode: "push"
        },
        resize: true
      },
      modes: {
        grab: {
          distance: 200,
          links: {
            opacity: 0.8
          }
        },
        push: {
          quantity: 4
        }
      }
    },
    detectRetina: true
  }), []);

  useEffect(() => {
    // Load external CSS files
    const cssFiles = [
      '/pluck-assets/css/bootstrap.rtl.v2.min.css',
      '/pluck-assets/css/animate.min.css',
      '/pluck-assets/css/fontawesome.min.css',
      '/pluck-assets/css/style.css',
      '/pluck-assets/css/responsive.css',
      '/pluck-assets/css/rtl.css'
    ];

    const linkElements = [];

    // Load each CSS file
    cssFiles.forEach(href => {
      // Check if already loaded
      if (document.querySelector(`link[href="${href}"]`)) return;
      
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = href;
      document.head.appendChild(link);
      linkElements.push(link);
    });

    // Handle scroll
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);

    // Handle ESC key for fullscreen video
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        setVideoFullscreen(false);
        setVideoModalOpen(false);
      }
    };
    window.addEventListener('keydown', handleKeyDown);

    // Cleanup
    return () => {
      linkElements.forEach(link => link.remove());
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  const handleFormSubmit = (e) => {
    e.preventDefault();
    setFormSubmitted(true);
    // Here you can add API call to submit lead
    setTimeout(() => setFormSubmitted(false), 3000);
  };

  const navItems = [
    { href: '#home', labelAr: 'الرئيسية', labelEn: 'Home' },
    { href: '#how-it-works', labelAr: 'كيف يعمل', labelEn: 'How it Works' },
    { href: '#services', labelAr: 'الخدمات', labelEn: 'Services' },
    { href: '#features', labelAr: 'المميزات', labelEn: 'Features' },
    { href: '#pricing', labelAr: 'الأسعار', labelEn: 'Pricing' },
    { href: '#testimonials', labelAr: 'آراء العملاء', labelEn: 'Testimonials' },
    { href: '#faq', labelAr: 'الأسئلة الشائعة', labelEn: 'FAQ' },
    { href: '#contact', labelAr: 'تواصل معنا', labelEn: 'Contact' },
  ];

  const services = [
    {
      icon: 'fas fa-chart-line',
      titleAr: 'مراقبة ذكية',
      titleEn: 'Smart Monitoring',
      descAr: 'تتبع وجمع البيانات من منصات التواصل الاجتماعي بشكل آلي',
      descEn: 'Automatically track and collect data from social platforms',
      color: '#6c63ff'
    },
    {
      icon: 'fas fa-bullhorn',
      titleAr: 'تسويق متقدم',
      titleEn: 'Advanced Marketing',
      descAr: 'أدوات تسويق احترافية للوصول لجمهورك المستهدف',
      descEn: 'Professional marketing tools to reach your target audience',
      color: '#00c9a7'
    },
    {
      icon: 'fas fa-robot',
      titleAr: 'ذكاء اصطناعي',
      titleEn: 'AI Powered',
      descAr: 'تحليل ذكي وإنشاء محتوى بالذكاء الاصطناعي',
      descEn: 'Smart analysis and AI-powered content creation',
      color: '#ff6b6b'
    },
    {
      icon: 'fas fa-shield-alt',
      titleAr: 'أمان عالي',
      titleEn: 'High Security',
      descAr: 'حماية بياناتك بأعلى معايير الأمان',
      descEn: 'Protect your data with highest security standards',
      color: '#ffc107'
    }
  ];

  const features = [
    { icon: 'fas fa-code', labelAr: 'كود نظيف', labelEn: 'Clean Code' },
    { icon: 'fas fa-mobile-alt', labelAr: 'تطبيق موبايل', labelEn: 'Mobile App' },
    { icon: 'fas fa-paint-brush', labelAr: 'تصميم احترافي', labelEn: 'Pro Design' },
    { icon: 'fas fa-headset', labelAr: 'دعم 24/7', labelEn: '24/7 Support' },
    { icon: 'fas fa-sync', labelAr: 'تحديثات مستمرة', labelEn: 'Regular Updates' },
    { icon: 'fas fa-globe', labelAr: 'متعدد اللغات', labelEn: 'Multi-Language' }
  ];

  const testimonials = [
    {
      nameAr: 'أحمد محمد',
      nameEn: 'Ahmed Mohamed',
      roleAr: 'مدير تسويق - السعودية',
      roleEn: 'Marketing Director - Saudi Arabia',
      countryFlag: '🇸🇦',
      textAr: 'منصة رائعة ساعدتني في زيادة مبيعاتي بنسبة 150% خلال 3 أشهر فقط. الدعم الفني ممتاز والأدوات سهلة الاستخدام.',
      textEn: 'Amazing platform that helped me increase sales by 150% in just 3 months. Excellent support and easy-to-use tools.',
      image: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=150&h=150&fit=crop&crop=face'
    },
    {
      nameAr: 'جيسيكا ويليامز',
      nameEn: 'Jessica Williams',
      roleAr: 'رائدة أعمال - أمريكا',
      roleEn: 'Entrepreneur - USA',
      countryFlag: '🇺🇸',
      textAr: 'أفضل منصة تسويق استخدمتها على الإطلاق! النتائج مذهلة والواجهة سهلة جداً.',
      textEn: 'The best marketing platform I have ever used! Amazing results and very intuitive interface.',
      image: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=150&h=150&fit=crop&crop=face'
    },
    {
      nameAr: 'لي وي',
      nameEn: 'Li Wei',
      roleAr: 'مدير عمليات - الصين',
      roleEn: 'Operations Manager - China',
      countryFlag: '🇨🇳',
      textAr: 'ساعدتني المنصة في إدارة حملاتي التسويقية بكفاءة عالية. أنصح بها لكل رجل أعمال.',
      textEn: 'This platform helped me manage my marketing campaigns with high efficiency. I recommend it to every businessman.',
      image: 'https://images.unsplash.com/photo-1556157382-97eda2d62296?w=150&h=150&fit=crop&crop=face'
    },
    {
      nameAr: 'ناتاليا إيفانوفا',
      nameEn: 'Natalia Ivanova',
      roleAr: 'مديرة تسويق رقمي - روسيا',
      roleEn: 'Digital Marketing Manager - Russia',
      countryFlag: '🇷🇺',
      textAr: 'أدوات تحليل البيانات رائعة! استطعت فهم عملائي بشكل أفضل وزيادة المبيعات.',
      textEn: 'Amazing data analytics tools! I was able to understand my customers better and increase sales.',
      image: 'https://images.unsplash.com/photo-1580489944761-15a19d654956?w=150&h=150&fit=crop&crop=face'
    },
    {
      nameAr: 'كوامي أسانتي',
      nameEn: 'Kwame Asante',
      roleAr: 'مؤسس شركة ناشئة - غانا',
      roleEn: 'Startup Founder - Ghana',
      countryFlag: '🇬🇭',
      textAr: 'المنصة غيرت طريقة عملي تماماً. الآن أستطيع الوصول لعملاء في جميع أنحاء أفريقيا.',
      textEn: 'This platform completely changed my workflow. Now I can reach customers across Africa.',
      image: 'https://images.unsplash.com/photo-1506794778202-cad84cf45f1d?w=150&h=150&fit=crop&crop=face'
    },
    {
      nameAr: 'ماريا غونزاليس',
      nameEn: 'Maria Gonzalez',
      roleAr: 'مديرة مبيعات - إسبانيا',
      roleEn: 'Sales Director - Spain',
      countryFlag: '🇪🇸',
      textAr: 'نتائج مبهرة في وقت قصير! الفريق الداعم محترف جداً ويستجيب بسرعة.',
      textEn: 'Impressive results in a short time! The support team is very professional and responds quickly.',
      image: 'https://images.unsplash.com/photo-1573497019940-1c28c88b4f3e?w=150&h=150&fit=crop&crop=face'
    },
    {
      nameAr: 'هيروشي تاناكا',
      nameEn: 'Hiroshi Tanaka',
      roleAr: 'مستشار أعمال - اليابان',
      roleEn: 'Business Consultant - Japan',
      countryFlag: '🇯🇵',
      textAr: 'منصة احترافية بكل المقاييس. أستخدمها يومياً وأنصح بها جميع عملائي.',
      textEn: 'A professional platform by all standards. I use it daily and recommend it to all my clients.',
      image: 'https://images.unsplash.com/photo-1560250097-0b93528c311a?w=150&h=150&fit=crop&crop=face'
    },
    {
      nameAr: 'فاطمة الزهراء',
      nameEn: 'Fatima Alzahra',
      roleAr: 'مديرة محتوى - الإمارات',
      roleEn: 'Content Manager - UAE',
      countryFlag: '🇦🇪',
      textAr: 'سهلت علي المنصة الوصول لجمهوري المستهدف. الأدوات متكاملة وسهلة الاستخدام.',
      textEn: 'The platform made it easy for me to reach my target audience. The tools are integrated and user-friendly.',
      image: 'https://images.unsplash.com/photo-1487412720507-e7ab37603c6f?w=150&h=150&fit=crop&crop=face'
    },
    {
      nameAr: 'ديفيد تشين',
      nameEn: 'David Chen',
      roleAr: 'مدير تقنية - سنغافورة',
      roleEn: 'Tech Director - Singapore',
      countryFlag: '🇸🇬',
      textAr: 'أفضل منصة تسويق استخدمتها. التكامل مع الأنظمة الأخرى سلس جداً.',
      textEn: 'Best marketing platform I have used. The integration with other systems is very smooth.',
      image: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=150&h=150&fit=crop&crop=face'
    }
  ];

  // Testimonials slider state
  const [testimonialIndex, setTestimonialIndex] = useState(0);
  const testimonialsPerPage = 3;
  const totalPages = Math.ceil(testimonials.length / testimonialsPerPage);

  // Auto-slide testimonials
  useEffect(() => {
    const interval = setInterval(() => {
      setTestimonialIndex((prev) => (prev + 1) % totalPages);
    }, 5000); // Change every 5 seconds
    return () => clearInterval(interval);
  }, [totalPages]);

  const nextTestimonials = () => {
    setTestimonialIndex((prev) => (prev + 1) % totalPages);
  };

  const prevTestimonials = () => {
    setTestimonialIndex((prev) => (prev - 1 + totalPages) % totalPages);
  };

  const visibleTestimonials = testimonials.slice(
    testimonialIndex * testimonialsPerPage,
    testimonialIndex * testimonialsPerPage + testimonialsPerPage
  );

  // Subscription Plans - Full data matching Subscription.js
  const subscriptionPlans = [
    {
      id: 'free',
      nameAr: 'مجانية',
      nameEn: 'Free',
      icon: 'fa-star',
      color: '#71717A',
      borderColor: '#ffffff',
      monthlyPrice: 0,
      yearlyPrice: 0,
      membersLimit: 100,
      trialDays: 30,
      megaReachAccess: '7_days',
      featuresAr: [
        '100 مشترك كحد أقصى',
        'شهر تجريبي مجاني',
        'منصة واحدة (Telegram)',
        'تصدير JSON فقط',
        'دعم بالبريد الإلكتروني'
      ],
      featuresEn: [
        'Up to 100 members',
        'Free trial month',
        'One platform (Telegram)',
        'JSON export only',
        'Email support'
      ],
      popular: false
    },
    {
      id: 'standard',
      nameAr: 'قياسية',
      nameEn: 'Standard',
      icon: 'fa-bolt',
      color: '#00F0FF',
      borderColor: '#888888',
      monthlyPrice: 24.99,
      yearlyPrice: 200.92,
      membersLimit: 100000,
      freeWeek: true,
      megaReachAccess: 'unlimited',
      featuresAr: [
        '100,000 مشترك',
        'أسبوع مجاني عند الاشتراك',
        'جميع المنصات الأربع',
        'تصدير CSV و JSON',
        'التصنيف الذكي بالـ AI'
      ],
      featuresEn: [
        '100,000 members',
        'Free week on subscription',
        'All four platforms',
        'CSV & JSON export',
        'AI smart classification'
      ],
      popular: false
    },
    {
      id: 'pro',
      nameAr: 'احترافية',
      nameEn: 'Pro',
      icon: 'fa-crown',
      color: '#7000FF',
      borderColor: '#00BFFF',
      monthlyPrice: 49.99,
      yearlyPrice: 401.92,
      membersLimit: 250000,
      freeMonth: true,
      megaReachAccess: 'unlimited',
      featuresAr: [
        '250,000 مشترك',
        'شهر مجاني للسنوي',
        'API كامل للتكامل',
        'تصنيف AI متقدم',
        'دعم 24/7'
      ],
      featuresEn: [
        '250,000 members',
        'Free month for annual',
        'Full API integration',
        'Advanced AI classification',
        '24/7 support'
      ],
      popular: true
    },
    {
      id: 'platinum',
      nameAr: 'بلاتينية',
      nameEn: 'Platinum',
      icon: 'fa-gem',
      color: '#FF9500',
      borderColor: '#FFB347',
      monthlyPrice: 99.99,
      yearlyPrice: 795.96,
      membersLimit: 500000,
      freeMonth: true,
      megaReachAccess: 'unlimited',
      featuresAr: [
        '500,000 مشترك',
        'شهر مجاني للسنوي',
        'مدير حساب مخصص',
        'تكاملات مخصصة',
        'SLA مضمون 99.9%'
      ],
      featuresEn: [
        '500,000 members',
        'Free month for annual',
        'Dedicated account manager',
        'Custom integrations',
        '99.9% SLA guaranteed'
      ],
      popular: false
    },
    {
      id: 'expert',
      nameAr: 'الخبراء',
      nameEn: 'Expert',
      icon: 'fa-shield-alt',
      color: '#FF003C',
      borderColor: '#FF6347',
      monthlyPrice: 149.99,
      yearlyPrice: 1206.92,
      membersLimit: 1000000,
      freeMonth: true,
      megaReachAccess: 'unlimited',
      featuresAr: [
        '1,000,000 مشترك',
        'شهر مجاني للسنوي',
        'خادم مخصص',
        'دعم VIP على مدار الساعة',
        'استشارات تسويقية'
      ],
      featuresEn: [
        '1,000,000 members',
        'Free month for annual',
        'Dedicated server',
        '24/7 VIP support',
        'Marketing consulting'
      ],
      popular: false
    }
  ];

  const faqs = [
    {
      qAr: 'كيف يمكنني البدء في استخدام المنصة؟',
      qEn: 'How can I start using the platform?',
      aAr: 'يمكنك التسجيل مجاناً والبدء فوراً. ستحصل على فترة تجريبية مجانية لاستكشاف جميع المميزات.',
      aEn: 'You can sign up for free and start immediately. You will get a free trial period to explore all features.'
    },
    {
      qAr: 'هل بياناتي آمنة؟',
      qEn: 'Is my data secure?',
      aAr: 'نعم، نستخدم أحدث تقنيات التشفير وأعلى معايير الأمان لحماية بياناتك.',
      aEn: 'Yes, we use the latest encryption technologies and highest security standards to protect your data.'
    },
    {
      qAr: 'هل يمكنني إلغاء اشتراكي في أي وقت؟',
      qEn: 'Can I cancel my subscription anytime?',
      aAr: 'نعم، يمكنك إلغاء اشتراكك في أي وقت دون أي رسوم إضافية.',
      aEn: 'Yes, you can cancel your subscription at any time without any additional fees.'
    },
    {
      qAr: 'هل هناك دعم فني؟',
      qEn: 'Is there technical support?',
      aAr: 'نعم، فريق الدعم متاح على مدار الساعة لمساعدتك.',
      aEn: 'Yes, our support team is available 24/7 to help you.'
    }
  ];

  const [openFaq, setOpenFaq] = useState(0);

  return (
    <div dir={isRTL ? 'rtl' : 'ltr'} style={{ 
      fontFamily: "'Segoe UI', Tahoma, sans-serif",
      overflowX: 'hidden',
      maxWidth: '100vw',
      width: '100%',
      position: 'relative'
    }}>
      {/* Navbar */}
      <nav style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 1000,
        background: isScrolled ? 'rgba(255,255,255,0.98)' : 'transparent',
        boxShadow: isScrolled ? '0 2px 20px rgba(0,0,0,0.1)' : 'none',
        padding: isScrolled ? '10px 0' : '20px 0',
        transition: 'all 0.3s ease'
      }}>
        <div style={{ width: "100%", maxWidth: "100%", paddingLeft: "5%", paddingRight: "5%", marginLeft: "0", marginRight: "0" }}>
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'space-between',
            flexWrap: 'wrap'
          }}>
            {/* Logo */}
            <Link to="/" style={{ 
              fontSize: '28px', 
              fontWeight: 'bold',
              color: isScrolled ? '#6c63ff' : '#fff',
              textDecoration: 'none'
            }}>
              oeia.io
            </Link>

            {/* Mobile Menu Button */}
            <button 
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="d-lg-none"
              style={{ 
                border: 'none', 
                background: 'transparent',
                cursor: 'pointer',
                padding: '10px'
              }}
            >
              <i className={`fas ${mobileMenuOpen ? 'fa-times' : 'fa-bars'}`} 
                 style={{ color: isScrolled ? '#333' : '#fff', fontSize: '24px' }}></i>
            </button>

            {/* Desktop Navigation */}
            <div className="d-none d-lg-flex" style={{ 
              alignItems: 'center', 
              gap: '30px'
            }}>
              {/* Nav Links */}
              <div style={{ display: 'flex', gap: '5px' }}>
                {navItems.map((item, idx) => (
                  <a key={idx} href={item.href} style={{
                    color: isScrolled ? '#333' : '#fff',
                    fontWeight: '500',
                    padding: '10px 12px',
                    transition: 'color 0.3s',
                    textDecoration: 'none',
                    fontSize: '14px'
                  }}>
                    {isRTL ? item.labelAr : item.labelEn}
                  </a>
                ))}
              </div>

              {/* Auth Buttons */}
              <div style={{ display: 'flex', gap: '10px' }}>
                <Link to="/login" style={{
                  padding: '10px 25px',
                  borderRadius: '30px',
                  background: 'transparent',
                  border: `2px solid ${isScrolled ? '#6c63ff' : '#fff'}`,
                  color: isScrolled ? '#6c63ff' : '#fff',
                  textDecoration: 'none',
                  fontWeight: '600',
                  transition: 'all 0.3s'
                }}>
                  {isRTL ? 'تسجيل الدخول' : 'Login'}
                </Link>
                <Link to="/register" style={{
                  padding: '10px 25px',
                  borderRadius: '30px',
                  background: '#6c63ff',
                  border: '2px solid #6c63ff',
                  color: '#fff',
                  textDecoration: 'none',
                  fontWeight: '600',
                  transition: 'all 0.3s'
                }}>
                  {isRTL ? 'ابدأ مجاناً' : 'Start Free'}
                </Link>
              </div>
            </div>
          </div>

          {/* Mobile Navigation */}
          {mobileMenuOpen && (
            <div className="d-lg-none" style={{
              background: '#fff',
              borderRadius: '15px',
              marginTop: '15px',
              padding: '20px',
              boxShadow: '0 10px 40px rgba(0,0,0,0.15)'
            }}>
              {navItems.map((item, idx) => (
                <a key={idx} href={item.href} 
                   onClick={() => setMobileMenuOpen(false)}
                   style={{
                     display: 'block',
                     color: '#333',
                     fontWeight: '500',
                     padding: '12px 15px',
                     textDecoration: 'none',
                     borderBottom: idx < navItems.length - 1 ? '1px solid #eee' : 'none'
                   }}>
                  {isRTL ? item.labelAr : item.labelEn}
                </a>
              ))}
              <div style={{ 
                display: 'flex', 
                gap: '10px', 
                marginTop: '15px',
                padding: '0 15px'
              }}>
                <Link to="/login" onClick={() => setMobileMenuOpen(false)} style={{
                  flex: 1,
                  padding: '12px',
                  borderRadius: '10px',
                  background: 'transparent',
                  border: '2px solid #6c63ff',
                  color: '#6c63ff',
                  textDecoration: 'none',
                  fontWeight: '600',
                  textAlign: 'center'
                }}>
                  {isRTL ? 'تسجيل الدخول' : 'Login'}
                </Link>
                <Link to="/register" onClick={() => setMobileMenuOpen(false)} style={{
                  flex: 1,
                  padding: '12px',
                  borderRadius: '10px',
                  background: '#6c63ff',
                  border: '2px solid #6c63ff',
                  color: '#fff',
                  textDecoration: 'none',
                  fontWeight: '600',
                  textAlign: 'center'
                }}>
                  {isRTL ? 'ابدأ مجاناً' : 'Start Free'}
                </Link>
              </div>
            </div>
          )}
        </div>
      </nav>

      {/* Hero Section */}
      <section id="home" style={{
        background: 'linear-gradient(135deg, #6c63ff 0%, #7c3aed 50%, #8b5cf6 100%)',
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        position: 'relative',
        overflow: 'hidden',
        paddingTop: '100px'
      }}>
        {/* Particles.js Background */}
        <Particles
          id="tsparticles"
          key="tsparticles-hero"
          init={particlesInit}
          loaded={particlesLoadedCallback}
          options={particlesOptions}
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            zIndex: 1,
            pointerEvents: 'auto'
          }}
        />

        <div style={{ width: "100%", maxWidth: "90%", margin: "0 auto", padding: "0", position: 'relative', zIndex: 10 }}>
          <div className="row align-items-center" style={{ margin: '0' }}>
            <div className="col-lg-6" style={{ paddingInlineEnd: '2%' }}>
              <div style={{ paddingInlineStart: '0' }}>
                <span style={{
                  background: 'rgba(255,255,255,0.2)',
                  padding: '8px 20px',
                  borderRadius: '30px',
                  fontSize: '14px',
                  fontWeight: '500',
                  display: 'inline-block',
                  marginBottom: '20px',
                  color: '#fff'
                }}>
                  {isRTL ? '🚀 منصة التسويق الأولى عربياً' : '🚀 #1 Marketing Platform'}
                </span>
                <h1 style={{
                  fontSize: 'clamp(32px, 4vw, 48px)',
                  fontWeight: '800',
                  lineHeight: '1.2',
                  marginBottom: '25px',
                  color: '#fff'
                }}>
                  {isRTL 
                    ? 'المنصة الأقوى لإدارة التسويق وجمع البيانات'
                    : 'The Most Powerful Platform for Marketing & Data Collection'}
                </h1>
                <p style={{
                  fontSize: '16px',
                  lineHeight: '1.8',
                  marginBottom: '35px',
                  maxWidth: '500px',
                  color: 'rgba(255,255,255,0.9)'
                }}>
                  {isRTL
                    ? 'أدوات متكاملة للمراقبة والتسويق مدعومة بالذكاء الاصطناعي. ابدأ مجاناً واكتشف كيف يمكننا مضاعفة نتائجك.'
                    : 'Integrated monitoring and marketing tools powered by AI. Start free and discover how we can multiply your results.'}
                </p>
                <div style={{ display: 'flex', gap: '15px', flexWrap: 'wrap' }}>
                  <Link to="/register" style={{
                    padding: '15px 35px',
                    borderRadius: '30px',
                    background: '#fff',
                    color: '#6c63ff',
                    textDecoration: 'none',
                    fontWeight: '700',
                    fontSize: '15px',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '10px',
                    boxShadow: '0 10px 30px rgba(0,0,0,0.2)'
                  }}>
                    <i className="fas fa-rocket"></i>
                    {isRTL ? 'ابدأ الآن مجاناً' : 'Start Free Now'}
                  </Link>
                  <button 
                    onClick={() => setVideoModalOpen(true)}
                    style={{
                      padding: '15px 35px',
                      borderRadius: '30px',
                      background: '#D01110',
                      border: 'none',
                      color: '#fff',
                      fontWeight: '600',
                      fontSize: '15px',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '10px',
                      cursor: 'pointer',
                      transition: 'all 0.3s'
                    }}>
                    <i className="fas fa-play-circle"></i>
                    {isRTL ? 'شاهد الفيديو' : 'Watch Video'}
                  </button>
                </div>
              </div>
            </div>

            <div className="col-lg-6" style={{ paddingInlineStart: '2%' }}>
              <div style={{
                background: '#fff',
                borderRadius: '20px',
                padding: '35px',
                boxShadow: '0 30px 60px rgba(0,0,0,0.3)',
                marginTop: '30px',
                width: '100%',
                maxWidth: '520px',
                marginInlineStart: 'auto'
              }}>
                <div style={{ textAlign: 'center', marginBottom: '25px' }}>
                  <h3 style={{ 
                    color: '#000', 
                    fontSize: '24px', 
                    fontWeight: '700',
                    marginBottom: '10px'
                  }}>
                    {isRTL ? 'ابدأ تجربتك المجانية' : 'Start Your Free Trial'}
                  </h3>
                  <p style={{ color: '#333', fontSize: '14px' }}>
                    {isRTL ? 'لا حاجة لبطاقة ائتمان' : 'No credit card required'}
                  </p>
                </div>

                {formSubmitted ? (
                  <div style={{
                    textAlign: 'center',
                    padding: '40px 20px',
                    color: '#00c9a7'
                  }}>
                    <i className="fas fa-check-circle" style={{ fontSize: '60px', marginBottom: '20px' }}></i>
                    <h4 style={{ color: '#000' }}>{isRTL ? 'شكراً لك!' : 'Thank You!'}</h4>
                    <p style={{ color: '#333' }}>{isRTL ? 'سنتواصل معك قريباً' : 'We will contact you soon'}</p>
                  </div>
                ) : (
                  <form onSubmit={handleFormSubmit}>
                    <div style={{ marginBottom: '15px' }}>
                      <input
                        type="text"
                        placeholder={isRTL ? 'الاسم الكامل' : 'Full Name'}
                        value={formData.name}
                        onChange={(e) => setFormData({...formData, name: e.target.value})}
                        required
                        style={{
                          width: '100%',
                          padding: '15px 20px',
                          borderRadius: '10px',
                          border: '2px solid #eee',
                          fontSize: '16px',
                          outline: 'none',
                          transition: 'border 0.3s',
                          color: '#000',
                          backgroundColor: '#fff'
                        }}
                      />
                    </div>
                    <div style={{ marginBottom: '15px' }}>
                      <input
                        type="email"
                        placeholder={isRTL ? 'البريد الإلكتروني' : 'Email Address'}
                        value={formData.email}
                        onChange={(e) => setFormData({...formData, email: e.target.value})}
                        required
                        style={{
                          width: '100%',
                          padding: '15px 20px',
                          borderRadius: '10px',
                          border: '2px solid #eee',
                          fontSize: '16px',
                          outline: 'none',
                          color: '#000',
                          backgroundColor: '#fff'
                        }}
                      />
                    </div>
                    <div style={{ marginBottom: '20px' }}>
                      <input
                        type="tel"
                        placeholder={isRTL ? 'رقم الهاتف' : 'Phone Number'}
                        value={formData.phone}
                        onChange={(e) => setFormData({...formData, phone: e.target.value})}
                        style={{
                          width: '100%',
                          padding: '15px 20px',
                          borderRadius: '10px',
                          border: '2px solid #eee',
                          fontSize: '16px',
                          outline: 'none',
                          color: '#000',
                          backgroundColor: '#fff'
                        }}
                      />
                    </div>
                    <button type="submit" style={{
                      width: '100%',
                      padding: '15px',
                      borderRadius: '10px',
                      background: 'linear-gradient(135deg, #6c63ff, #4834d4)',
                      color: '#fff',
                      border: 'none',
                      fontSize: '16px',
                      fontWeight: '700',
                      cursor: 'pointer',
                      transition: 'transform 0.3s'
                    }}>
                      {isRTL ? 'ابدأ الآن' : 'Get Started'}
                    </button>
                  </form>
                )}

                <p style={{ 
                  textAlign: 'center', 
                  marginTop: '15px', 
                  fontSize: '12px', 
                  color: '#666'
                }}>
                  <i className="fas fa-lock" style={{ marginLeft: isRTL ? '5px' : '0', marginRight: isRTL ? '0' : '5px' }}></i>
                  {isRTL ? 'بياناتك آمنة ولن نشاركها مع أحد' : 'Your data is safe and will never be shared'}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Partners Section */}
      <section style={{ 
        padding: '60px 0', 
        background: '#f8f9fa',
        borderBottom: '1px solid #eee'
      }}>
        <div style={{ width: "100%", maxWidth: "100%", paddingLeft: "5%", paddingRight: "5%", marginLeft: "0", marginRight: "0" }}>
          <p style={{ textAlign: 'center', color: '#666', marginBottom: '30px' }}>
            {isRTL ? 'موثوق من قبل أكثر من 2.5 مليون شركة' : 'Trusted by over 2.5 million companies'}
          </p>
          <div style={{ 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center', 
            gap: '50px',
            flexWrap: 'wrap'
          }}>
            {[
              { icon: 'fab fa-google', name: 'Google' },
              { icon: 'fab fa-microsoft', name: 'Microsoft' },
              { icon: 'fab fa-amazon', name: 'Amazon' },
              { icon: 'fab fa-slack', name: 'Slack' },
              { icon: 'fab fa-shopify', name: 'Shopify' },
              { icon: 'fab fa-stripe', name: 'Stripe' }
            ].map((partner, idx) => (
              <div key={idx} style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: '8px',
                color: '#888',
                transition: 'color 0.3s'
              }}>
                <i className={partner.icon} style={{ fontSize: '28px' }}></i>
                <span style={{ fontSize: '18px', fontWeight: '600' }}>{partner.name}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How it Works Section */}
      <section id="how-it-works" style={{ padding: '100px 0', background: '#fff' }}>
        <div style={{ width: "100%", maxWidth: "100%", paddingLeft: "5%", paddingRight: "5%", marginLeft: "0", marginRight: "0" }}>
          <div style={{ textAlign: 'center', marginBottom: '60px' }}>
            <span style={{
              background: '#6c63ff',
              color: '#fff',
              padding: '12px 28px',
              borderRadius: '30px',
              fontSize: '16px',
              fontWeight: '700'
            }}>
              {isRTL ? 'كيف يعمل' : 'How it Works'}
            </span>
            <h2 style={{
              fontSize: 'clamp(28px, 4vw, 42px)',
              fontWeight: '800',
              color: '#222',
              marginTop: '20px',
              marginBottom: '15px'
            }}>
              {isRTL ? 'ثلاث خطوات بسيطة للبدء' : 'Three Simple Steps to Get Started'}
            </h2>
            <p style={{ color: '#666', maxWidth: '600px', margin: '0 auto', fontSize: '16px' }}>
              {isRTL 
                ? 'ابدأ رحلتك معنا في دقائق معدودة'
                : 'Start your journey with us in just minutes'}
            </p>
          </div>

          <div className="row justify-content-center">
            {/* Step 1 */}
            <div className="col-lg-4 col-md-6" style={{ marginBottom: '30px' }}>
              <div style={{
                background: '#fff',
                borderRadius: '20px',
                padding: '40px 30px',
                textAlign: 'center',
                boxShadow: '0 10px 40px rgba(0,0,0,0.08)',
                position: 'relative',
                height: '100%'
              }}>
                <div style={{
                  width: '80px',
                  height: '80px',
                  borderRadius: '50%',
                  background: 'linear-gradient(135deg, #6c63ff, #8b5cf6)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 25px',
                  color: '#fff',
                  fontSize: '32px',
                  fontWeight: '800'
                }}>
                  1
                </div>
                <h4 style={{ fontWeight: '700', marginBottom: '15px', color: '#222' }}>
                  {isRTL ? 'سجّل حسابك' : 'Create Account'}
                </h4>
                <p style={{ color: '#666', fontSize: '14px', lineHeight: '1.7' }}>
                  {isRTL 
                    ? 'أنشئ حسابك المجاني في ثوانٍ معدودة وابدأ استكشاف المنصة'
                    : 'Create your free account in seconds and start exploring the platform'}
                </p>
              </div>
            </div>

            {/* Step 2 */}
            <div className="col-lg-4 col-md-6" style={{ marginBottom: '30px' }}>
              <div style={{
                background: '#fff',
                borderRadius: '20px',
                padding: '40px 30px',
                textAlign: 'center',
                boxShadow: '0 10px 40px rgba(0,0,0,0.08)',
                position: 'relative',
                height: '100%'
              }}>
                <div style={{
                  width: '80px',
                  height: '80px',
                  borderRadius: '50%',
                  background: 'linear-gradient(135deg, #00c9a7, #00b894)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 25px',
                  color: '#fff',
                  fontSize: '32px',
                  fontWeight: '800'
                }}>
                  2
                </div>
                <h4 style={{ fontWeight: '700', marginBottom: '15px', color: '#222' }}>
                  {isRTL ? 'اختر خطتك' : 'Choose Your Plan'}
                </h4>
                <p style={{ color: '#666', fontSize: '14px', lineHeight: '1.7' }}>
                  {isRTL 
                    ? 'اختر الخطة المناسبة لاحتياجاتك من بين خططنا المتنوعة'
                    : 'Select the plan that suits your needs from our variety of options'}
                </p>
              </div>
            </div>

            {/* Step 3 */}
            <div className="col-lg-4 col-md-6" style={{ marginBottom: '30px' }}>
              <div style={{
                background: '#fff',
                borderRadius: '20px',
                padding: '40px 30px',
                textAlign: 'center',
                boxShadow: '0 10px 40px rgba(0,0,0,0.08)',
                position: 'relative',
                height: '100%'
              }}>
                <div style={{
                  width: '80px',
                  height: '80px',
                  borderRadius: '50%',
                  background: 'linear-gradient(135deg, #ff6b6b, #ee5a5a)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 25px',
                  color: '#fff',
                  fontSize: '32px',
                  fontWeight: '800'
                }}>
                  3
                </div>
                <h4 style={{ fontWeight: '700', marginBottom: '15px', color: '#222' }}>
                  {isRTL ? 'ابدأ النمو' : 'Start Growing'}
                </h4>
                <p style={{ color: '#666', fontSize: '14px', lineHeight: '1.7' }}>
                  {isRTL 
                    ? 'استخدم أدواتنا المتقدمة وشاهد نتائجك تتضاعف'
                    : 'Use our advanced tools and watch your results multiply'}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" style={{ padding: '100px 0', background: '#fff' }}>
        <div style={{ width: "100%", maxWidth: "100%", paddingLeft: "5%", paddingRight: "5%", marginLeft: "0", marginRight: "0" }}>
          <div style={{ textAlign: 'center', marginBottom: '60px' }}>
            <span style={{
              background: '#6c63ff',
              color: '#fff',
              padding: '12px 28px',
              borderRadius: '30px',
              fontSize: '16px',
              fontWeight: '700'
            }}>
              {isRTL ? 'خدماتنا' : 'Our Services'}
            </span>
            <h2 style={{
              fontSize: 'clamp(28px, 4vw, 42px)',
              fontWeight: '800',
              color: '#222',
              marginTop: '20px',
              marginBottom: '15px'
            }}>
              {isRTL ? 'نقدم لك حلولاً متكاملة' : 'We Offer Complete Solutions'}
            </h2>
            <p style={{ color: '#666', maxWidth: '600px', margin: '0 auto', fontSize: '16px' }}>
              {isRTL 
                ? 'مجموعة شاملة من الأدوات المصممة لتحقيق أهدافك التسويقية'
                : 'A comprehensive set of tools designed to achieve your marketing goals'}
            </p>
          </div>

          <div className="row">
            {services.map((service, idx) => (
              <div key={idx} className="col-lg-3 col-md-6" style={{ marginBottom: '30px' }}>
                <div style={{
                  background: '#fff',
                  borderRadius: '20px',
                  padding: '40px 30px',
                  textAlign: 'center',
                  boxShadow: '0 10px 40px rgba(0,0,0,0.08)',
                  transition: 'transform 0.3s, box-shadow 0.3s',
                  height: '100%',
                  cursor: 'pointer'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-10px)';
                  e.currentTarget.style.boxShadow = '0 20px 50px rgba(0,0,0,0.15)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 10px 40px rgba(0,0,0,0.08)';
                }}>
                  <div style={{
                    width: '80px',
                    height: '80px',
                    borderRadius: '50%',
                    background: `${service.color}15`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    margin: '0 auto 25px'
                  }}>
                    <i className={service.icon} style={{ fontSize: '32px', color: service.color }}></i>
                  </div>
                  <h4 style={{ fontWeight: '700', marginBottom: '15px', color: '#222' }}>
                    {isRTL ? service.titleAr : service.titleEn}
                  </h4>
                  <p style={{ color: '#666', fontSize: '14px', lineHeight: '1.7' }}>
                    {isRTL ? service.descAr : service.descEn}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" style={{ 
        padding: '100px 0', 
        background: 'linear-gradient(135deg, #f8f9ff 0%, #fff 100%)'
      }}>
        <div style={{ width: "100%", maxWidth: "100%", paddingLeft: "5%", paddingRight: "5%", marginLeft: "0", marginRight: "0" }}>
          <div className="row align-items-center">
            <div className="col-lg-6">
              <span style={{
                background: '#6c63ff',
                color: '#fff',
                padding: '12px 28px',
                borderRadius: '30px',
                fontSize: '16px',
                fontWeight: '700'
              }}>
                {isRTL ? 'المميزات' : 'Features'}
              </span>
              <h2 style={{
                fontSize: 'clamp(28px, 4vw, 42px)',
                fontWeight: '800',
                color: '#222',
                marginTop: '20px',
                marginBottom: '20px'
              }}>
                {isRTL ? 'كل ما تحتاجه في مكان واحد' : 'Everything You Need in One Place'}
              </h2>
              <p style={{ color: '#666', fontSize: '16px', lineHeight: '1.8', marginBottom: '30px' }}>
                {isRTL
                  ? 'منصة متكاملة تجمع بين أدوات المراقبة والتسويق والتحليل، مصممة خصيصاً لتلبية احتياجاتك.'
                  : 'An integrated platform combining monitoring, marketing, and analytics tools, designed specifically for your needs.'}
              </p>

              <div className="row">
                {features.map((feature, idx) => (
                  <div key={idx} className="col-6 col-md-4" style={{ marginBottom: '25px' }}>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px'
                    }}>
                      <div style={{
                        width: '45px',
                        height: '45px',
                        borderRadius: '12px',
                        background: '#6c63ff15',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}>
                        <i className={feature.icon} style={{ color: '#6c63ff', fontSize: '18px' }}></i>
                      </div>
                      <span style={{ fontWeight: '600', color: '#333', fontSize: '14px' }}>
                        {isRTL ? feature.labelAr : feature.labelEn}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="col-lg-6" style={{ textAlign: 'center' }}>
              <img 
                src="https://images.unsplash.com/photo-1552581234-26160f608093?w=560&h=400&fit=crop" 
                alt="Features" 
                style={{ maxWidth: '100%', height: 'auto', borderRadius: '20px', boxShadow: '0 20px 50px rgba(0,0,0,0.15)' }}
              />
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section id="testimonials" style={{ padding: '100px 0', background: '#fff' }}>
        <div style={{ width: "100%", maxWidth: "100%", paddingLeft: "5%", paddingRight: "5%", marginLeft: "0", marginRight: "0" }}>
          <div style={{ textAlign: 'center', marginBottom: '60px' }}>
            <span style={{
              background: '#6c63ff',
              color: '#fff',
              padding: '12px 28px',
              borderRadius: '30px',
              fontSize: '16px',
              fontWeight: '700'
            }}>
              {isRTL ? 'آراء العملاء' : 'Testimonials'}
            </span>
            <h2 style={{
              fontSize: 'clamp(28px, 4vw, 42px)',
              fontWeight: '800',
              color: '#222',
              marginTop: '20px'
            }}>
              {isRTL ? 'ماذا يقول عملاؤنا حول العالم' : 'What Our Global Customers Say'}
            </h2>
            <p style={{ color: '#666', marginTop: '10px' }}>
              {isRTL ? 'عملاء من أكثر من 50 دولة يثقون بنا' : 'Customers from over 50 countries trust us'}
            </p>
          </div>

          {/* Slider Container */}
          <div style={{ position: 'relative' }}>
            {/* Previous Button */}
            <button
              onClick={prevTestimonials}
              style={{
                position: 'absolute',
                top: '50%',
                left: isRTL ? 'auto' : '-20px',
                right: isRTL ? '-20px' : 'auto',
                transform: 'translateY(-50%)',
                width: '50px',
                height: '50px',
                borderRadius: '50%',
                background: '#6c63ff',
                color: '#fff',
                border: 'none',
                cursor: 'pointer',
                boxShadow: '0 5px 20px rgba(108, 99, 255, 0.4)',
                zIndex: 10,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'transform 0.3s'
              }}
            >
              <i className={`fas fa-chevron-${isRTL ? 'right' : 'left'}`}></i>
            </button>

            {/* Next Button */}
            <button
              onClick={nextTestimonials}
              style={{
                position: 'absolute',
                top: '50%',
                right: isRTL ? 'auto' : '-20px',
                left: isRTL ? '-20px' : 'auto',
                transform: 'translateY(-50%)',
                width: '50px',
                height: '50px',
                borderRadius: '50%',
                background: '#6c63ff',
                color: '#fff',
                border: 'none',
                cursor: 'pointer',
                boxShadow: '0 5px 20px rgba(108, 99, 255, 0.4)',
                zIndex: 10,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'transform 0.3s'
              }}
            >
              <i className={`fas fa-chevron-${isRTL ? 'left' : 'right'}`}></i>
            </button>

            {/* Testimonials Cards */}
            <div className="row" style={{ 
              transition: 'all 0.5s ease',
              padding: '0 30px'
            }}>
              {visibleTestimonials.map((t, idx) => (
                <div key={idx} className="col-lg-4 col-md-6" style={{ marginBottom: '30px' }}>
                  <div style={{
                    background: '#fff',
                    borderRadius: '20px',
                    padding: '40px',
                    boxShadow: '0 10px 40px rgba(0,0,0,0.08)',
                    height: '100%',
                    position: 'relative',
                    transition: 'transform 0.3s, box-shadow 0.3s'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-5px)';
                    e.currentTarget.style.boxShadow = '0 20px 50px rgba(0,0,0,0.15)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 10px 40px rgba(0,0,0,0.08)';
                  }}>
                    {/* Quote Icon */}
                    <div style={{
                      position: 'absolute',
                      top: '20px',
                      left: isRTL ? 'auto' : '30px',
                      right: isRTL ? '30px' : 'auto',
                      fontSize: '60px',
                      color: '#6c63ff20',
                      fontFamily: 'serif'
                    }}>&ldquo;</div>

                    {/* Country Flag */}
                    <div style={{
                      position: 'absolute',
                      top: '20px',
                      right: isRTL ? 'auto' : '25px',
                      left: isRTL ? '25px' : 'auto',
                      fontSize: '24px'
                    }}>
                      {t.countryFlag}
                    </div>

                    {/* Testimonial Text */}
                    <p style={{ 
                      color: '#555', 
                      fontSize: '15px', 
                      lineHeight: '1.8',
                      marginBottom: '25px',
                      position: 'relative',
                      zIndex: 1,
                      minHeight: '80px'
                    }}>
                      {isRTL ? t.textAr : t.textEn}
                    </p>

                    {/* Customer Info */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                      <img src={t.image} alt={isRTL ? t.nameAr : t.nameEn} style={{
                        width: '75px',
                        height: '75px',
                        borderRadius: '50%',
                        objectFit: 'cover',
                        border: '3px solid #6c63ff20'
                      }} />
                      <div>
                        <h5 style={{ margin: 0, fontWeight: '700', color: '#222', fontSize: '16px' }}>
                          {isRTL ? t.nameAr : t.nameEn}
                        </h5>
                        <small style={{ color: '#6c63ff', fontWeight: '500' }}>
                          {isRTL ? t.roleAr : t.roleEn}
                        </small>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Pagination Dots */}
            <div style={{ 
              display: 'flex', 
              justifyContent: 'center', 
              gap: '10px',
              marginTop: '30px'
            }}>
              {Array.from({ length: totalPages }).map((_, idx) => (
                <button
                  key={idx}
                  onClick={() => setTestimonialIndex(idx)}
                  style={{
                    width: testimonialIndex === idx ? '30px' : '12px',
                    height: '12px',
                    borderRadius: '6px',
                    background: testimonialIndex === idx ? '#6c63ff' : '#ddd',
                    border: 'none',
                    cursor: 'pointer',
                    transition: 'all 0.3s'
                  }}
                />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" style={{ 
        padding: '100px 0', 
        background: 'linear-gradient(135deg, #6c63ff 0%, #4834d4 100%)',
        width: '100%',
        maxWidth: '100vw',
        overflowX: 'hidden'
      }}>
        <div style={{ width: "100%", maxWidth: "100%", paddingLeft: "5%", paddingRight: "5%", marginLeft: "0", marginRight: "0" }}>
          <div style={{ textAlign: 'center', marginBottom: '40px' }}>
            <span style={{
              background: '#D01110',
              color: '#fff',
              padding: '12px 28px',
              borderRadius: '30px',
              fontSize: '16px',
              fontWeight: '700'
            }}>
              {isRTL ? 'خطط الاشتراك' : 'Subscription Plans'}
            </span>
            <h2 style={{
              fontSize: 'clamp(28px, 4vw, 42px)',
              fontWeight: '800',
              color: '#fff',
              marginTop: '20px'
            }}>
              {isRTL ? 'اختر الخطة المناسبة لاحتياجاتك' : 'Choose the Right Plan for Your Needs'}
            </h2>
          </div>

          {/* Billing Toggle */}
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center', 
            gap: '15px',
            marginBottom: '50px'
          }}>
            <span style={{ 
              color: !isYearlyBilling ? '#fff' : 'rgba(255,255,255,0.5)',
              fontWeight: '600'
            }}>
              {isRTL ? 'شهري' : 'Monthly'}
            </span>
            <button 
              onClick={() => setIsYearlyBilling(!isYearlyBilling)}
              style={{
                width: '60px',
                height: '30px',
                borderRadius: '15px',
                background: isYearlyBilling ? '#fff' : 'rgba(255,255,255,0.3)',
                border: 'none',
                cursor: 'pointer',
                position: 'relative',
                transition: 'background 0.3s'
              }}
            >
              <span style={{
                position: 'absolute',
                top: '3px',
                left: isYearlyBilling ? '33px' : '3px',
                width: '24px',
                height: '24px',
                borderRadius: '50%',
                background: isYearlyBilling ? '#6c63ff' : '#fff',
                transition: 'left 0.3s'
              }}></span>
            </button>
            <span style={{ 
              color: isYearlyBilling ? '#fff' : 'rgba(255,255,255,0.5)',
              fontWeight: '600'
            }}>
              {isRTL ? 'سنوي' : 'Yearly'}
            </span>
            {isYearlyBilling && (
              <span style={{
                background: '#D01110',
                color: '#fff',
                padding: '4px 12px',
                borderRadius: '20px',
                fontSize: '12px',
                fontWeight: '600'
              }}>
                {isRTL ? 'وفّر 33%' : 'Save 33%'}
              </span>
            )}
          </div>

          {/* Pricing Cards Grid */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
            gap: '20px',
            maxWidth: '1400px',
            margin: '0 auto'
          }}>
            {subscriptionPlans.map((plan, idx) => {
              const price = isYearlyBilling ? plan.yearlyPrice : plan.monthlyPrice;
              const originalYearlyPrice = plan.monthlyPrice * 12;
              const savings = isYearlyBilling ? (originalYearlyPrice - plan.yearlyPrice).toFixed(2) : 0;
              
              return (
                <div 
                  key={idx} 
                  style={{
                    background: 'rgba(75, 55, 160, 0.9)',
                    backdropFilter: 'blur(10px)',
                    borderRadius: '20px',
                    padding: '30px 25px',
                    border: `3px solid ${plan.borderColor}`,
                    position: 'relative',
                    transition: 'transform 0.3s, box-shadow 0.3s',
                    boxShadow: `0 5px 20px rgba(0,0,0,0.2)`
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-10px)';
                    e.currentTarget.style.boxShadow = `0 20px 40px rgba(0,0,0,0.3)`;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = `0 5px 20px rgba(0,0,0,0.2)`;
                  }}
                >
                  {/* Popular Badge */}
                  {plan.popular && (
                    <div style={{
                      position: 'absolute',
                      top: '-12px',
                      left: '50%',
                      transform: 'translateX(-50%)',
                      background: '#fff',
                      color: '#6c63ff',
                      padding: '5px 20px',
                      borderRadius: '20px',
                      fontSize: '12px',
                      fontWeight: '700'
                    }}>
                      {isRTL ? 'الأكثر شعبية' : 'Most Popular'}
                    </div>
                  )}

                  {/* Plan Icon & Name */}
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '12px', marginBottom: '20px' }}>
                    <div style={{
                      width: '50px',
                      height: '50px',
                      borderRadius: '12px',
                      background: 'rgba(255,255,255,0.2)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <i className={`fas ${plan.icon}`} style={{ color: '#fff', fontSize: '22px' }}></i>
                    </div>
                    <h4 style={{ color: '#fff', fontWeight: '700', fontSize: '20px', margin: 0 }}>
                      {isRTL ? plan.nameAr : plan.nameEn}
                    </h4>
                  </div>

                  {/* Price */}
                  <div style={{ marginBottom: '15px', textAlign: 'center' }}>
                    <span style={{ fontSize: '36px', fontWeight: '800', color: '#fff' }}>
                      ${price.toFixed(2)}
                    </span>
                    <span style={{ color: 'rgba(255,255,255,0.7)', fontSize: '14px' }}>
                      {isYearlyBilling ? (isRTL ? '/سنوياً' : '/year') : (isRTL ? '/شهرياً' : '/month')}
                    </span>
                  </div>

                  {/* Savings Badge */}
                  {isYearlyBilling && savings > 0 && (
                    <p style={{ color: '#a7f3d0', fontSize: '13px', marginBottom: '15px', textAlign: 'center' }}>
                      {isRTL ? `وفّر $${savings} (خصم 33%)` : `Save $${savings} (33% off)`}
                    </p>
                  )}

                  {/* Members Limit */}
                  <div style={{
                    background: 'rgba(255,255,255,0.1)',
                    borderRadius: '10px',
                    padding: '12px',
                    marginBottom: '15px'
                  }}>
                    <p style={{ color: 'rgba(255,255,255,0.7)', fontSize: '12px', margin: 0 }}>
                      {isRTL ? 'الحد الأقصى للأعضاء' : 'Max Members'}
                    </p>
                    <p style={{ color: '#fff', fontSize: '24px', fontWeight: '700', margin: 0 }}>
                      {plan.membersLimit.toLocaleString()}
                    </p>
                  </div>

                  {/* Mega-Reachy Badge */}
                  <div style={{
                    background: plan.megaReachAccess === 'unlimited' 
                      ? 'rgba(16, 185, 129, 0.25)' 
                      : '#D01110',
                    border: plan.megaReachAccess === 'unlimited' 
                      ? '1px solid #10b98160' 
                      : 'none',
                    borderRadius: '10px',
                    padding: '12px',
                    marginBottom: '15px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px'
                  }}>
                    <i className="fas fa-bullhorn" style={{ 
                      color: '#fff',
                      fontSize: '16px'
                    }}></i>
                    <div>
                      <p style={{ 
                        color: '#fff',
                        fontSize: '13px',
                        fontWeight: '600',
                        margin: 0
                      }}>
                        {isRTL ? 'منصة ميجا ريتشي' : 'Mega-Reachy Platform'}
                      </p>
                      <p style={{ 
                        color: '#fff',
                        fontSize: '11px',
                        margin: 0
                      }}>
                        {plan.megaReachAccess === 'unlimited' 
                          ? (isRTL ? '✓ مجانية بالكامل' : '✓ Full Free Access')
                          : (isRTL ? '⏱ أسبوع مجاني فقط' : '⏱ Free week only')}
                      </p>
                    </div>
                  </div>

                  {/* Free Period Badge */}
                  {(plan.freeWeek || plan.freeMonth) && (
                    <div style={{
                      background: '#10b981',
                      border: 'none',
                      borderRadius: '10px',
                      padding: '12px',
                      marginBottom: '15px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '10px'
                    }}>
                      <i className="fas fa-gift" style={{ 
                        color: '#fff',
                        fontSize: '16px'
                      }}></i>
                      <div>
                        <p style={{ 
                          color: '#fff',
                          fontSize: '13px',
                          fontWeight: '700',
                          margin: 0
                        }}>
                          🎁 {isRTL ? 'عرض خاص!' : 'Special Offer!'}
                        </p>
                        <p style={{ 
                          color: 'rgba(255,255,255,0.9)',
                          fontSize: '11px',
                          margin: 0
                        }}>
                          {plan.freeWeek 
                            ? (isRTL ? 'أسبوع مجاني' : 'Free week')
                            : (isRTL ? 'شهر مجاني' : 'Free month')}
                          {isYearlyBilling ? (isRTL ? ' عند الاشتراك السنوي' : ' with annual') : ''}
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Features List */}
                  <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 20px 0' }}>
                    {(isRTL ? plan.featuresAr : plan.featuresEn).slice(0, 5).map((feature, i) => (
                      <li key={i} style={{ 
                        display: 'flex', 
                        alignItems: 'flex-start', 
                        gap: '10px',
                        padding: '8px 0',
                        fontSize: '13px',
                        color: 'rgba(255,255,255,0.9)'
                      }}>
                        <i className="fas fa-check" style={{ color: '#a7f3d0', marginTop: '3px', fontSize: '12px' }}></i>
                        {feature}
                      </li>
                    ))}
                  </ul>

                  {/* CTA Button */}
                  <Link 
                    to="/register" 
                    style={{
                      display: 'block',
                      padding: '14px',
                      borderRadius: '12px',
                      background: plan.borderColor,
                      color: plan.id === 'free' ? '#333' : '#fff',
                      textDecoration: 'none',
                      fontWeight: '700',
                      fontSize: '15px',
                      textAlign: 'center',
                      transition: 'all 0.3s'
                    }}
                  >
                    {plan.id === 'free' 
                      ? (isRTL ? 'ابدأ مجاناً' : 'Start Free')
                      : (isRTL ? 'اشترك الآن' : 'Subscribe Now')}
                  </Link>
                </div>
              );
            })}
          </div>

          {/* Detailed Comparison Link */}
          <div style={{ textAlign: 'center', marginTop: '50px' }}>
            <button 
              onClick={() => setShowPlanComparison(true)}
              style={{
                background: 'rgba(255,255,255,0.2)',
                border: 'none',
                color: '#fff',
                fontSize: '15px',
                cursor: 'pointer',
                padding: '12px 25px',
                borderRadius: '30px',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px'
              }}
            >
              <i className="fas fa-table"></i>
              {isRTL ? 'مقارنة تفصيلية بين الخطط' : 'Detailed Plan Comparison'}
            </button>
          </div>

          {/* Payment Methods */}
          <div style={{
            background: 'rgba(255,255,255,0.15)',
            borderRadius: '20px',
            padding: '30px',
            maxWidth: '700px',
            margin: '50px auto 0'
          }}>
            <h4 style={{ 
              color: '#fff', 
              textAlign: 'center', 
              marginBottom: '20px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '10px'
            }}>
              <i className="fas fa-wallet" style={{ color: '#fff' }}></i>
              {isRTL ? 'طرق الدفع المتاحة' : 'Available Payment Methods'}
            </h4>
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: 'repeat(4, 1fr)', 
              gap: '15px' 
            }}>
              {[
                { name: 'USDT', network: 'TRC20', color: '#26a17b' },
                { name: 'BTC', network: 'Bitcoin', color: '#f7931a' },
                { name: 'ETH', network: 'Ethereum', color: '#627eea' },
                { name: 'BNB', network: 'BSC', color: '#f3ba2f' }
              ].map((crypto, i) => (
                <div key={i} style={{
                  background: 'rgba(255,255,255,0.1)',
                  borderRadius: '10px',
                  padding: '15px 10px',
                  textAlign: 'center'
                }}>
                  <p style={{ color: '#fff', fontWeight: '700', fontSize: '16px', margin: 0 }}>
                    {crypto.name}
                  </p>
                  <p style={{ color: 'rgba(255,255,255,0.7)', fontSize: '11px', margin: 0 }}>
                    {crypto.network}
                  </p>
                </div>
              ))}
            </div>
            <p style={{ 
              color: 'rgba(255,255,255,0.7)', 
              fontSize: '12px', 
              textAlign: 'center',
              marginTop: '15px'
            }}>
              {isRTL ? 'العملة المفضلة: USDT على شبكة TRC20 (رسوم أقل)' : 'Preferred: USDT on TRC20 network (lower fees)'}
            </p>
          </div>

          {/* Money Back Guarantee */}
          <div style={{ textAlign: 'center', marginTop: '30px' }}>
            <span style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              background: 'rgba(255, 255, 255, 0.2)',
              border: '1px solid rgba(255, 255, 255, 0.3)',
              borderRadius: '30px',
              padding: '10px 25px',
              color: '#fff',
              fontSize: '14px'
            }}>
              <i className="fas fa-shield-alt"></i>
              {isRTL ? 'ضمان استرداد الأموال خلال 7 أيام' : '7-day Money Back Guarantee'}
            </span>
          </div>
        </div>
      </section>

      {/* Plan Comparison Modal */}
      {showPlanComparison && (
        <div 
          onClick={() => setShowPlanComparison(false)}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.9)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
            padding: '20px'
          }}
        >
          <div 
            onClick={(e) => e.stopPropagation()}
            style={{
              background: '#1a1a2e',
              borderRadius: '20px',
              padding: '30px',
              maxWidth: '1200px',
              width: '100%',
              maxHeight: '90vh',
              overflow: 'auto',
              position: 'relative'
            }}
          >
            {/* Close Button */}
            <button
              onClick={() => setShowPlanComparison(false)}
              style={{
                position: 'absolute',
                top: '15px',
                right: isRTL ? 'auto' : '15px',
                left: isRTL ? '15px' : 'auto',
                width: '40px',
                height: '40px',
                borderRadius: '50%',
                background: '#ff6b6b',
                color: '#fff',
                border: 'none',
                cursor: 'pointer',
                fontSize: '18px'
              }}
            >
              <i className="fas fa-times"></i>
            </button>

            <h3 style={{ 
              color: '#fff', 
              textAlign: 'center', 
              marginBottom: '30px',
              fontSize: '24px'
            }}>
              {isRTL ? 'مقارنة تفصيلية بين الخطط' : 'Detailed Plan Comparison'}
            </h3>

            {/* Comparison Table */}
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
                    <th style={{ padding: '15px', textAlign: isRTL ? 'right' : 'left', color: 'rgba(255,255,255,0.6)' }}>
                      {isRTL ? 'الميزة' : 'Feature'}
                    </th>
                    {subscriptionPlans.map((plan, i) => (
                      <th key={i} style={{ padding: '15px', textAlign: 'center' }}>
                        <i className={`fas ${plan.icon}`} style={{ color: plan.color, fontSize: '20px', display: 'block', marginBottom: '5px' }}></i>
                        <span style={{ color: plan.color }}>{isRTL ? plan.nameAr : plan.nameEn}</span>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {/* Price Row */}
                  <tr style={{ background: 'rgba(255,255,255,0.03)' }}>
                    <td style={{ padding: '12px', color: '#fff', fontWeight: '600' }}>{isRTL ? 'السعر الشهري' : 'Monthly Price'}</td>
                    {subscriptionPlans.map((plan, i) => (
                      <td key={i} style={{ padding: '12px', textAlign: 'center', color: plan.color }}>
                        {plan.monthlyPrice === 0 ? (isRTL ? 'مجاناً' : 'Free') : `$${plan.monthlyPrice}`}
                      </td>
                    ))}
                  </tr>
                  {/* Members Row */}
                  <tr>
                    <td style={{ padding: '12px', color: '#fff', fontWeight: '600' }}>{isRTL ? 'الحد الأقصى للأعضاء' : 'Max Members'}</td>
                    {subscriptionPlans.map((plan, i) => (
                      <td key={i} style={{ padding: '12px', textAlign: 'center', color: '#fff' }}>
                        {plan.membersLimit.toLocaleString()}
                      </td>
                    ))}
                  </tr>
                  {/* Mega-Reachy Row */}
                  <tr style={{ background: 'rgba(16, 185, 129, 0.1)' }}>
                    <td style={{ padding: '12px', color: '#fff', fontWeight: '600' }}>
                      <i className="fas fa-bullhorn" style={{ color: '#10b981', marginLeft: isRTL ? '0' : '8px', marginRight: isRTL ? '8px' : '0' }}></i>
                      {isRTL ? 'منصة ميجا ريتشي' : 'Mega-Reachy'}
                    </td>
                    {subscriptionPlans.map((plan, i) => (
                      <td key={i} style={{ padding: '12px', textAlign: 'center', color: plan.megaReachAccess === 'unlimited' ? '#10b981' : '#f59e0b' }}>
                        {plan.megaReachAccess === 'unlimited' ? (isRTL ? '✓ مجانية' : '✓ Free') : (isRTL ? 'أسبوع فقط' : '1 Week')}
                      </td>
                    ))}
                  </tr>
                  {/* Free Period Row */}
                  <tr>
                    <td style={{ padding: '12px', color: '#fff', fontWeight: '600' }}>{isRTL ? 'الفترة المجانية' : 'Free Period'}</td>
                    {subscriptionPlans.map((plan, i) => (
                      <td key={i} style={{ padding: '12px', textAlign: 'center', color: '#10b981' }}>
                        {plan.trialDays ? (isRTL ? 'شهر تجريبي' : 'Trial Month') : 
                         plan.freeWeek ? (isRTL ? 'أسبوع' : 'Week') : 
                         plan.freeMonth ? (isRTL ? 'شهر' : 'Month') : '-'}
                      </td>
                    ))}
                  </tr>
                  {/* Platforms Row */}
                  <tr style={{ background: 'rgba(255,255,255,0.03)' }}>
                    <td style={{ padding: '12px', color: '#fff', fontWeight: '600' }}>{isRTL ? 'المنصات المدعومة' : 'Platforms'}</td>
                    <td style={{ padding: '12px', textAlign: 'center', color: '#fff' }}>Telegram</td>
                    <td style={{ padding: '12px', textAlign: 'center', color: '#fff' }}>{isRTL ? '4 منصات' : '4 Platforms'}</td>
                    <td style={{ padding: '12px', textAlign: 'center', color: '#fff' }}>{isRTL ? '4 + أولوية' : '4 + Priority'}</td>
                    <td style={{ padding: '12px', textAlign: 'center', color: '#fff' }}>{isRTL ? '4 + أولوية' : '4 + Priority'}</td>
                    <td style={{ padding: '12px', textAlign: 'center', color: '#fff' }}>{isRTL ? '4 + أولوية' : '4 + Priority'}</td>
                  </tr>
                  {/* AI Classification */}
                  <tr>
                    <td style={{ padding: '12px', color: '#fff', fontWeight: '600' }}>{isRTL ? 'التصنيف الذكي AI' : 'AI Classification'}</td>
                    <td style={{ padding: '12px', textAlign: 'center' }}><i className="fas fa-times" style={{ color: '#ef4444' }}></i></td>
                    <td style={{ padding: '12px', textAlign: 'center' }}><i className="fas fa-check" style={{ color: '#10b981' }}></i></td>
                    <td style={{ padding: '12px', textAlign: 'center' }}><i className="fas fa-check" style={{ color: '#10b981' }}></i></td>
                    <td style={{ padding: '12px', textAlign: 'center' }}><i className="fas fa-check" style={{ color: '#10b981' }}></i></td>
                    <td style={{ padding: '12px', textAlign: 'center' }}><i className="fas fa-check" style={{ color: '#10b981' }}></i></td>
                  </tr>
                  {/* API */}
                  <tr style={{ background: 'rgba(255,255,255,0.03)' }}>
                    <td style={{ padding: '12px', color: '#fff', fontWeight: '600' }}>{isRTL ? 'API للتكامل' : 'API Integration'}</td>
                    <td style={{ padding: '12px', textAlign: 'center' }}><i className="fas fa-times" style={{ color: '#ef4444' }}></i></td>
                    <td style={{ padding: '12px', textAlign: 'center' }}><i className="fas fa-times" style={{ color: '#ef4444' }}></i></td>
                    <td style={{ padding: '12px', textAlign: 'center' }}><i className="fas fa-check" style={{ color: '#10b981' }}></i></td>
                    <td style={{ padding: '12px', textAlign: 'center' }}><i className="fas fa-check" style={{ color: '#10b981' }}></i></td>
                    <td style={{ padding: '12px', textAlign: 'center' }}><i className="fas fa-check" style={{ color: '#10b981' }}></i></td>
                  </tr>
                  {/* Support */}
                  <tr>
                    <td style={{ padding: '12px', color: '#fff', fontWeight: '600' }}>{isRTL ? 'الدعم الفني' : 'Support'}</td>
                    <td style={{ padding: '12px', textAlign: 'center', color: '#fff' }}>{isRTL ? 'بريد' : 'Email'}</td>
                    <td style={{ padding: '12px', textAlign: 'center', color: '#fff' }}>{isRTL ? 'أولوية' : 'Priority'}</td>
                    <td style={{ padding: '12px', textAlign: 'center', color: '#fff' }}>24/7</td>
                    <td style={{ padding: '12px', textAlign: 'center', color: '#fff' }}>24/7</td>
                    <td style={{ padding: '12px', textAlign: 'center', color: '#fff' }}>VIP 24/7</td>
                  </tr>
                  {/* Account Manager */}
                  <tr style={{ background: 'rgba(255,255,255,0.03)' }}>
                    <td style={{ padding: '12px', color: '#fff', fontWeight: '600' }}>{isRTL ? 'مدير حساب مخصص' : 'Account Manager'}</td>
                    <td style={{ padding: '12px', textAlign: 'center' }}><i className="fas fa-times" style={{ color: '#ef4444' }}></i></td>
                    <td style={{ padding: '12px', textAlign: 'center' }}><i className="fas fa-times" style={{ color: '#ef4444' }}></i></td>
                    <td style={{ padding: '12px', textAlign: 'center' }}><i className="fas fa-times" style={{ color: '#ef4444' }}></i></td>
                    <td style={{ padding: '12px', textAlign: 'center' }}><i className="fas fa-check" style={{ color: '#10b981' }}></i></td>
                    <td style={{ padding: '12px', textAlign: 'center' }}><i className="fas fa-check" style={{ color: '#10b981' }}></i></td>
                  </tr>
                  {/* Dedicated Server */}
                  <tr>
                    <td style={{ padding: '12px', color: '#fff', fontWeight: '600' }}>{isRTL ? 'خادم مخصص' : 'Dedicated Server'}</td>
                    <td style={{ padding: '12px', textAlign: 'center' }}><i className="fas fa-times" style={{ color: '#ef4444' }}></i></td>
                    <td style={{ padding: '12px', textAlign: 'center' }}><i className="fas fa-times" style={{ color: '#ef4444' }}></i></td>
                    <td style={{ padding: '12px', textAlign: 'center' }}><i className="fas fa-times" style={{ color: '#ef4444' }}></i></td>
                    <td style={{ padding: '12px', textAlign: 'center' }}><i className="fas fa-times" style={{ color: '#ef4444' }}></i></td>
                    <td style={{ padding: '12px', textAlign: 'center' }}><i className="fas fa-check" style={{ color: '#10b981' }}></i></td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* Close Button */}
            <div style={{ textAlign: 'center', marginTop: '30px' }}>
              <button
                onClick={() => setShowPlanComparison(false)}
                style={{
                  padding: '12px 40px',
                  borderRadius: '10px',
                  background: '#6c63ff',
                  color: '#fff',
                  border: 'none',
                  fontWeight: '600',
                  cursor: 'pointer'
                }}
              >
                {isRTL ? 'إغلاق' : 'Close'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* FAQ Section */}
      <section id="faq" style={{ padding: '100px 0', background: '#fff' }}>
        <div style={{ width: "100%", maxWidth: "100%", paddingLeft: "5%", paddingRight: "5%", marginLeft: "0", marginRight: "0" }}>
          <div style={{ textAlign: 'center', marginBottom: '60px' }}>
            <span style={{
              background: '#6c63ff',
              color: '#fff',
              padding: '12px 28px',
              borderRadius: '30px',
              fontSize: '16px',
              fontWeight: '700'
            }}>
              {isRTL ? 'الأسئلة الشائعة' : 'FAQ'}
            </span>
            <h2 style={{
              fontSize: 'clamp(28px, 4vw, 42px)',
              fontWeight: '800',
              color: '#222',
              marginTop: '20px'
            }}>
              {isRTL ? 'الأسئلة الأكثر شيوعاً' : 'Frequently Asked Questions'}
            </h2>
          </div>

          <div style={{ maxWidth: '800px', margin: '0 auto' }}>
            {faqs.map((faq, idx) => (
              <div key={idx} style={{
                marginBottom: '15px',
                borderRadius: '15px',
                overflow: 'hidden',
                boxShadow: '0 5px 20px rgba(0,0,0,0.05)'
              }}>
                <button
                  onClick={() => setOpenFaq(openFaq === idx ? -1 : idx)}
                  style={{
                    width: '100%',
                    padding: '20px 25px',
                    background: openFaq === idx ? '#6c63ff' : '#fff',
                    color: openFaq === idx ? '#fff' : '#333',
                    border: 'none',
                    textAlign: isRTL ? 'right' : 'left',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    cursor: 'pointer',
                    fontWeight: '600',
                    fontSize: '16px',
                    transition: 'all 0.3s'
                  }}
                >
                  {isRTL ? faq.qAr : faq.qEn}
                  <i className={`fas fa-${openFaq === idx ? 'minus' : 'plus'}`}></i>
                </button>
                {openFaq === idx && (
                  <div style={{
                    padding: '20px 25px',
                    background: '#f8f9fa',
                    color: '#666',
                    lineHeight: '1.8'
                  }}>
                    {isRTL ? faq.aAr : faq.aEn}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" style={{ padding: '100px 0', background: '#fff' }}>
        <div style={{ width: "100%", maxWidth: "100%", paddingLeft: "5%", paddingRight: "5%", marginLeft: "0", marginRight: "0" }}>
          <div style={{ textAlign: 'center', marginBottom: '60px' }}>
            <span style={{
              background: '#6c63ff',
              color: '#fff',
              padding: '12px 28px',
              borderRadius: '30px',
              fontSize: '16px',
              fontWeight: '700'
            }}>
              {isRTL ? 'تواصل معنا' : 'Contact Us'}
            </span>
            <h2 style={{
              fontSize: 'clamp(28px, 4vw, 42px)',
              fontWeight: '800',
              color: '#222',
              marginTop: '20px'
            }}>
              {isRTL ? 'نحن هنا لمساعدتك' : 'We are Here to Help'}
            </h2>
          </div>

          <div className="row justify-content-center">
            {/* Email */}
            <div className="col-lg-4 col-md-6" style={{ marginBottom: '30px' }}>
              <div style={{
                background: '#fff',
                borderRadius: '20px',
                padding: '40px 30px',
                textAlign: 'center',
                boxShadow: '0 10px 40px rgba(0,0,0,0.08)',
                height: '100%'
              }}>
                <div style={{
                  width: '80px',
                  height: '80px',
                  borderRadius: '50%',
                  background: '#6c63ff15',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 20px'
                }}>
                  <i className="fas fa-envelope" style={{ fontSize: '32px', color: '#6c63ff' }}></i>
                </div>
                <h4 style={{ fontWeight: '700', color: '#222', marginBottom: '10px' }}>
                  {isRTL ? 'البريد الإلكتروني' : 'Email'}
                </h4>
                <p style={{ color: '#666', fontSize: '16px' }}>support@oeia.io</p>
              </div>
            </div>

            {/* Telegram */}
            <div className="col-lg-4 col-md-6" style={{ marginBottom: '30px' }}>
              <div style={{
                background: '#fff',
                borderRadius: '20px',
                padding: '40px 30px',
                textAlign: 'center',
                boxShadow: '0 10px 40px rgba(0,0,0,0.08)',
                height: '100%'
              }}>
                <div style={{
                  width: '80px',
                  height: '80px',
                  borderRadius: '50%',
                  background: '#0088cc15',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 20px'
                }}>
                  <i className="fab fa-telegram-plane" style={{ fontSize: '32px', color: '#0088cc' }}></i>
                </div>
                <h4 style={{ fontWeight: '700', color: '#222', marginBottom: '10px' }}>
                  {isRTL ? 'تيليجرام' : 'Telegram'}
                </h4>
                <p style={{ color: '#666', fontSize: '16px' }}>@oeia_support</p>
              </div>
            </div>

            {/* Location */}
            <div className="col-lg-4 col-md-6" style={{ marginBottom: '30px' }}>
              <div style={{
                background: '#fff',
                borderRadius: '20px',
                padding: '40px 30px',
                textAlign: 'center',
                boxShadow: '0 10px 40px rgba(0,0,0,0.08)',
                height: '100%'
              }}>
                <div style={{
                  width: '80px',
                  height: '80px',
                  borderRadius: '50%',
                  background: '#ff6b6b15',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 20px'
                }}>
                  <i className="fas fa-map-marker-alt" style={{ fontSize: '32px', color: '#ff6b6b' }}></i>
                </div>
                <h4 style={{ fontWeight: '700', color: '#222', marginBottom: '10px' }}>
                  {isRTL ? 'الموقع' : 'Location'}
                </h4>
                <p style={{ color: '#666', fontSize: '16px' }}>
                  {isRTL ? 'المملكة المتحدة - لندن' : 'UNITED KINGDOM - London'}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section style={{
        padding: '100px 0',
        background: 'linear-gradient(135deg, #6c63ff 0%, #4834d4 100%)',
        textAlign: 'center'
      }}>
        <div style={{ width: "100%", maxWidth: "100%", paddingLeft: "5%", paddingRight: "5%", marginLeft: "0", marginRight: "0" }}>
          <h2 style={{
            color: '#fff',
            fontSize: 'clamp(28px, 4vw, 42px)',
            fontWeight: '800',
            marginBottom: '20px'
          }}>
            {isRTL ? 'مستعد للبدء؟' : 'Ready to Get Started?'}
          </h2>
          <p style={{ color: 'rgba(255,255,255,0.9)', marginBottom: '30px', fontSize: '18px' }}>
            {isRTL 
              ? 'انضم لآلاف المستخدمين الذين يثقون بنا'
              : 'Join thousands of users who trust us'}
          </p>
          <Link to="/register" style={{
            padding: '18px 50px',
            borderRadius: '30px',
            background: '#fff',
            color: '#6c63ff',
            textDecoration: 'none',
            fontWeight: '700',
            fontSize: '18px',
            display: 'inline-flex',
            alignItems: 'center',
            gap: '10px',
            boxShadow: '0 10px 30px rgba(0,0,0,0.2)'
          }}>
            <i className="fas fa-rocket"></i>
            {isRTL ? 'ابدأ الآن مجاناً' : 'Start Free Now'}
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer style={{ 
        padding: '50px 0 30px', 
        background: '#222',
        color: '#fff'
      }}>
        <div style={{ width: "100%", maxWidth: "100%", paddingLeft: "5%", paddingRight: "5%", marginLeft: "0", marginRight: "0" }}>
          <div className="row align-items-center">
            <div className="col-lg-6">
              <div style={{ marginBottom: '20px' }}>
                <span style={{ fontSize: '28px', fontWeight: '800' }}>oeia.io</span>
                <p style={{ color: '#999', marginTop: '10px', fontSize: '14px' }}>
                  {isRTL 
                    ? 'منصة التسويق والمراقبة الأولى عربياً'
                    : 'The #1 Arabic Marketing & Monitoring Platform'}
                </p>
              </div>
            </div>
            <div className="col-lg-6">
              <div style={{ 
                display: 'flex', 
                gap: '15px', 
                justifyContent: isRTL ? 'flex-start' : 'flex-end'
              }}>
                {['facebook-f', 'twitter', 'instagram', 'linkedin-in'].map((icon, idx) => (
                  <a key={idx} href="#" style={{
                    width: '40px',
                    height: '40px',
                    borderRadius: '50%',
                    background: 'rgba(255,255,255,0.1)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#fff',
                    textDecoration: 'none',
                    transition: 'background 0.3s'
                  }}>
                    <i className={`fab fa-${icon}`}></i>
                  </a>
                ))}
              </div>
            </div>
          </div>
          <hr style={{ borderColor: 'rgba(255,255,255,0.1)', margin: '30px 0 20px' }} />
          <p style={{ textAlign: 'center', color: '#666', fontSize: '14px', margin: 0 }}>
            © 2026 oeia.io - {isRTL ? 'جميع الحقوق محفوظة' : 'All Rights Reserved'}
          </p>
        </div>
      </footer>

      {/* Go to Top Button */}
      {isScrolled && (
        <button
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          style={{
            position: 'fixed',
            bottom: '30px',
            left: isRTL ? '30px' : 'auto',
            right: isRTL ? 'auto' : '30px',
            width: '50px',
            height: '50px',
            borderRadius: '50%',
            background: '#6c63ff',
            color: '#fff',
            border: 'none',
            cursor: 'pointer',
            boxShadow: '0 5px 20px rgba(108, 99, 255, 0.4)',
            zIndex: 999
          }}
        >
          <i className="fas fa-arrow-up"></i>
        </button>
      )}

      {/* Video Modal */}
      {videoModalOpen && (
        <div 
          onClick={() => setVideoModalOpen(false)}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.85)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
            padding: '20px'
          }}
        >
          <div 
            onClick={(e) => e.stopPropagation()}
            style={{
              background: '#fff',
              borderRadius: '20px',
              padding: '30px',
              maxWidth: '900px',
              width: '100%',
              position: 'relative',
              boxShadow: '0 30px 60px rgba(0,0,0,0.3)'
            }}
          >
            {/* Close Button */}
            <button
              onClick={() => setVideoModalOpen(false)}
              style={{
                position: 'absolute',
                top: '-15px',
                right: isRTL ? 'auto' : '-15px',
                left: isRTL ? '-15px' : 'auto',
                width: '40px',
                height: '40px',
                borderRadius: '50%',
                background: '#ff6b6b',
                color: '#fff',
                border: 'none',
                cursor: 'pointer',
                fontSize: '18px',
                boxShadow: '0 5px 15px rgba(255,107,107,0.4)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <i className="fas fa-times"></i>
            </button>

            {/* Modal Header */}
            <div style={{ textAlign: 'center', marginBottom: '25px' }}>
              <h3 style={{ color: '#222', fontSize: '24px', fontWeight: '700', marginBottom: '10px' }}>
                {isRTL ? 'شاهد كيف تعمل المنصة' : 'See How the Platform Works'}
              </h3>
              <p style={{ color: '#666', fontSize: '14px' }}>
                {isRTL ? 'فيديو توضيحي في أقل من دقيقة' : 'Explainer video in less than a minute'}
              </p>
            </div>

            {/* Video Placeholder */}
            <div style={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              borderRadius: '15px',
              aspectRatio: '16/9',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#fff',
              position: 'relative',
              overflow: 'hidden'
            }}>
              {/* Decorative circles */}
              <div style={{
                position: 'absolute',
                top: '-50px',
                right: '-50px',
                width: '150px',
                height: '150px',
                borderRadius: '50%',
                background: 'rgba(255,255,255,0.1)'
              }}></div>
              <div style={{
                position: 'absolute',
                bottom: '-30px',
                left: '-30px',
                width: '100px',
                height: '100px',
                borderRadius: '50%',
                background: 'rgba(255,255,255,0.1)'
              }}></div>

              {/* Play icon */}
              <div style={{
                width: '80px',
                height: '80px',
                borderRadius: '50%',
                background: 'rgba(255,255,255,0.2)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginBottom: '20px',
                cursor: 'pointer',
                transition: 'transform 0.3s'
              }}>
                <i className="fas fa-play" style={{ fontSize: '30px', marginLeft: '5px' }}></i>
              </div>

              <h4 style={{ fontSize: '20px', fontWeight: '600', marginBottom: '10px', color: '#fff' }}>
                {isRTL ? 'الفيديو قريباً' : 'Video Coming Soon'}
              </h4>
              <p style={{ fontSize: '14px', opacity: 0.9, color: '#fff' }}>
                {isRTL ? 'نعمل على إعداد فيديو توضيحي رائع' : 'We are preparing an amazing explainer video'}
              </p>

              {/* Fullscreen Button */}
              <button
                onClick={() => setVideoFullscreen(true)}
                style={{
                  position: 'absolute',
                  bottom: '15px',
                  right: isRTL ? 'auto' : '15px',
                  left: isRTL ? '15px' : 'auto',
                  width: '40px',
                  height: '40px',
                  borderRadius: '10px',
                  background: 'rgba(255,255,255,0.2)',
                  color: '#fff',
                  border: 'none',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'background 0.3s'
                }}
                title={isRTL ? 'ملء الشاشة' : 'Fullscreen'}
              >
                <i className="fas fa-expand"></i>
              </button>

              {/* 
                TODO: Replace this placeholder with actual video embed
                Example for YouTube:
                <iframe 
                  width="100%" 
                  height="100%" 
                  src="https://www.youtube.com/embed/YOUR_VIDEO_ID" 
                  frameBorder="0" 
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" 
                  allowFullScreen
                  style={{ borderRadius: '15px' }}
                ></iframe>
              */}
            </div>

            {/* Modal Footer */}
            <div style={{ 
              textAlign: 'center', 
              marginTop: '20px',
              display: 'flex',
              gap: '15px',
              justifyContent: 'center'
            }}>
              <button
                onClick={() => setVideoModalOpen(false)}
                style={{
                  padding: '12px 30px',
                  borderRadius: '10px',
                  background: '#f0f0f0',
                  color: '#333',
                  border: 'none',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.3s'
                }}
              >
                {isRTL ? 'إغلاق' : 'Close'}
              </button>
              <Link
                to="/register"
                onClick={() => setVideoModalOpen(false)}
                style={{
                  padding: '12px 30px',
                  borderRadius: '10px',
                  background: 'linear-gradient(135deg, #6c63ff, #8b5cf6)',
                  color: '#fff',
                  border: 'none',
                  fontWeight: '600',
                  textDecoration: 'none',
                  transition: 'all 0.3s'
                }}
              >
                {isRTL ? 'ابدأ الآن مجاناً' : 'Start Free Now'}
              </Link>
            </div>
          </div>
        </div>
      )}

      {/* Fullscreen Video Modal */}
      {videoFullscreen && (
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: '#000',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 99999
          }}
        >
          {/* Exit Fullscreen Button */}
          <button
            onClick={() => setVideoFullscreen(false)}
            style={{
              position: 'absolute',
              top: '20px',
              right: isRTL ? 'auto' : '20px',
              left: isRTL ? '20px' : 'auto',
              width: '50px',
              height: '50px',
              borderRadius: '50%',
              background: 'rgba(255,255,255,0.2)',
              color: '#fff',
              border: 'none',
              cursor: 'pointer',
              fontSize: '20px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'background 0.3s',
              zIndex: 100000
            }}
            title={isRTL ? 'إغلاق ملء الشاشة' : 'Exit Fullscreen'}
          >
            <i className="fas fa-compress"></i>
          </button>

          {/* Fullscreen Video Placeholder */}
          <div style={{
            width: '100%',
            height: '100%',
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#fff'
          }}>
            {/* Decorative circles */}
            <div style={{
              position: 'absolute',
              top: '-100px',
              right: '-100px',
              width: '300px',
              height: '300px',
              borderRadius: '50%',
              background: 'rgba(255,255,255,0.05)'
            }}></div>
            <div style={{
              position: 'absolute',
              bottom: '-80px',
              left: '-80px',
              width: '250px',
              height: '250px',
              borderRadius: '50%',
              background: 'rgba(255,255,255,0.05)'
            }}></div>

            {/* Play icon */}
            <div style={{
              width: '120px',
              height: '120px',
              borderRadius: '50%',
              background: 'rgba(255,255,255,0.2)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginBottom: '30px',
              cursor: 'pointer'
            }}>
              <i className="fas fa-play" style={{ fontSize: '50px', marginLeft: '8px' }}></i>
            </div>

            <h2 style={{ fontSize: '36px', fontWeight: '700', marginBottom: '15px', color: '#fff' }}>
              {isRTL ? 'الفيديو قريباً' : 'Video Coming Soon'}
            </h2>
            <p style={{ fontSize: '18px', opacity: 0.9, color: '#fff' }}>
              {isRTL ? 'نعمل على إعداد فيديو توضيحي رائع' : 'We are preparing an amazing explainer video'}
            </p>

            {/* Press ESC hint */}
            <p style={{ 
              position: 'absolute', 
              bottom: '30px', 
              fontSize: '14px', 
              opacity: 0.6,
              color: '#fff'
            }}>
              {isRTL ? 'اضغط ESC أو زر الإغلاق للخروج' : 'Press ESC or close button to exit'}
            </p>

            {/* 
              TODO: Replace this placeholder with actual video embed
              Example for YouTube in fullscreen:
              <iframe 
                width="100%" 
                height="100%" 
                src="https://www.youtube.com/embed/YOUR_VIDEO_ID?autoplay=1" 
                frameBorder="0" 
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" 
                allowFullScreen
              ></iframe>
            */}
          </div>
        </div>
      )}
    </div>
  );
}
